import { Box, Flex } from '@chakra-ui/react';
import type { FlexProps } from '@chakra-ui/react';
import type { ReactNode } from 'react';

export interface ContainerStackProps extends Omit<FlexProps, 'direction'> {
  /** Container width (px) at/above which children lay out in a row. Default 512 (32rem). */
  threshold?: number;
  children: ReactNode;
}

/**
 * A Stack-like primitive that reflows column -> row based on the width of THIS
 * container, not the viewport. Use instead of `direction={{ base:'column',
 * md:'row' }}` inside any pane that can shrink independently of the window
 * (settings content, split-view, chat panel open, modals). See
 * `components/settings/billing/PlanSelector.tsx` for the canonical container-query
 * usage this generalises.
 */
export function ContainerStack({
  threshold = 512,
  gap = 3,
  children,
  ...flexProps
}: ContainerStackProps) {
  return (
    <Box sx={{ containerType: 'inline-size' }} w="100%">
      {/* `gap` is destructured out of flexProps so the spread below can't
          re-clobber the resolved default with an `undefined`. */}
      <Flex
        direction="column"
        gap={gap}
        sx={{
          [`@container (min-width: ${threshold}px)`]: {
            flexDirection: 'row',
            alignItems: 'center',
          },
        }}
        {...flexProps}
      >
        {children}
      </Flex>
    </Box>
  );
}
